import api from '@/services/api.service';
import type { RejectionPayload } from '@/services/rejection.service';

function unwrap<T>(res: unknown): T {
  const data = (res as any)?.data ?? res;
  return (data?.data ?? data) as T;
}

export interface StockAdjustmentReasonCode {
  id: string;
  code: string;
  label: string;
  category: string;
  sort_order: number;
}

export interface StockAdjustmentLine {
  id?: string;
  line_number: number;
  item_id: string;
  item_code?: string;
  item_description?: string;
  warehouse_id?: string | null;
  location_id?: string | null;
  location_label?: string | null;
  system_quantity: number;
  actual_quantity: number;
  variance_quantity?: number;
  reserved_quantity?: number;
  uom?: string;
  unit_cost?: number;
  variance_value?: number;
  batch_number?: string | null;
  heat_number?: string | null;
  dimension?: string | null;
  mtc_document_id?: string | null;
  mtc_document_number?: string | null;
  mtc_reference_number?: string | null;
  linked_mtc_document_id?: string | null;
  linked_mtc_document_number?: string | null;
  linked_mtc_reference_number?: string | null;
  serial_number?: string | null;
  line_reason_code?: string | null;
  notes?: string | null;
  item?: { item_code: string; item_name: string };
}

export interface StockAdjustment {
  id: string;
  company_id: string;
  adjustment_number: string;
  reference_number?: string | null;
  adjustment_date: string;
  posting_date?: string | null;
  adjustment_type: string;
  warehouse_id: string;
  department_id?: string | null;
  reason: string;
  reason_code?: string | null;
  adjustment_status: string;
  approval_status: string;
  approval_instance_id?: string | null;
  is_posted: boolean;
  posted_date?: string | null;
  journal_voucher_id?: string | null;
  notes?: string | null;
  warehouse?: { id: string; warehouse_name: string; warehouse_code: string };
  items?: StockAdjustmentLine[];
}

class StockAdjustmentService {
  async listReasonCodes(): Promise<StockAdjustmentReasonCode[]> {
    const res = await api.getClient().get('/inventory/stock-adjustments/reason-codes');
    return unwrap<StockAdjustmentReasonCode[]>(res);
  }

  async list(params?: Record<string, string>): Promise<StockAdjustment[]> {
    const res = await api.getClient().get('/inventory/stock-adjustments', { params });
    return unwrap<StockAdjustment[]>(res);
  }

  async getById(id: string): Promise<StockAdjustment> {
    const res = await api.getClient().get(`/inventory/stock-adjustments/${id}`);
    return unwrap<StockAdjustment>(res);
  }

  async create(body: Record<string, unknown>): Promise<StockAdjustment> {
    const res = await api.getClient().post('/inventory/stock-adjustments', body);
    return unwrap<StockAdjustment>(res);
  }

  async update(id: string, body: Record<string, unknown>): Promise<StockAdjustment> {
    const res = await api.getClient().put(`/inventory/stock-adjustments/${id}`, body);
    return unwrap<StockAdjustment>(res);
  }

  async submit(id: string): Promise<StockAdjustment> {
    const res = await api.getClient().post(`/inventory/stock-adjustments/${id}/submit`);
    return unwrap<StockAdjustment>(res);
  }

  async approve(id: string, remarks?: string): Promise<StockAdjustment> {
    const res = await api.getClient().post(`/inventory/stock-adjustments/${id}/approve`, {
      remarks: remarks ?? undefined,
    });
    return unwrap<StockAdjustment>(res);
  }

  async rejectApproval(id: string, payload: RejectionPayload): Promise<StockAdjustment> {
    const res = await api.getClient().post(`/inventory/stock-adjustments/${id}/reject-approval`, payload);
    return unwrap<StockAdjustment>(res);
  }

  async post(id: string): Promise<StockAdjustment> {
    const res = await api.getClient().post(`/inventory/stock-adjustments/${id}/post`);
    return unwrap<StockAdjustment>(res);
  }

  async cancel(id: string): Promise<StockAdjustment> {
    const res = await api.getClient().post(`/inventory/stock-adjustments/${id}/cancel`);
    return unwrap<StockAdjustment>(res);
  }
}

export default new StockAdjustmentService();
